import * as React from 'react';

import { ClientLogger } from 'bernie-client';

import { LocalizedText, useLocalization } from '@shared-ui/localization-context';
import { useEgClickstream } from '@shared-ui/clickstream-analytics-context';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { classNames } from 'uitk-react-utils';

import { useStores } from 'hooks';

import { searchWidgetSearchSelectedEvent } from 'modules/analytics';
import { getAtlasDirectTrackingLink, getAtlasPartnerizeTrackingLink } from 'modules/tracking';

import {
  Branding,
  Dates,
  Destinations,
  getSearchUrl,
  Submit,
  Title,
  useSearchWidgetContext,
} from 'features/search-widget';

const logger = ClientLogger.getLoggerWithIdentifier('products.search-widget.flights-form');

function FlightsForm(): JSX.Element {
  const {
    app: {
      productId,
      program,
      tracking,
      network: { id: networkId },
      analyticData: { referrerIdPrefix },
    },
  } = useStores();

  const l10n = useLocalization();

  const { lobs, search, selectedLob, fromDestination, toDestination, startDate, endDate } = useSearchWidgetContext();

  const [isLoading, setIsLoading] = React.useState(false);

  const track = useEgClickstream();

  async function handleSubmit(event) {
    event.preventDefault();

    setIsLoading(true);

    // Setup empty popup early after user interaction to prevent browser block
    window['popup'] = window.open('', '_blank');

    const searchUrl = getSearchUrl({
      search,
      selectedLob,
      fromDestination,
      toDestination,
      startDate,
      endDate,
    });

    let atlasUrl;

    switch (networkId) {
      case 'direct':
        atlasUrl = await getAtlasDirectTrackingLink({
          mdpcid: tracking.mdpcid,
          landingPages: [
            {
              label: productId,
              url: `${searchUrl}&mdpcid=${tracking.mdpcid}${tracking.rffrid ? `&rffrid=${tracking.rffrid}` : ''}`,
            },
          ],
          logger,
        });
        break;

      case 'pz':
        atlasUrl = await getAtlasPartnerizeTrackingLink({
          tracking,
          landingPages: [{ label: productId, url: searchUrl, generateShortLink: false }],
          logger,
        });
        break;
    }

    const url = (atlasUrl && atlasUrl[0]?.url) || searchUrl;

    if (url) {
      setIsLoading(false);
    }

    searchWidgetSearchSelectedEvent(track, program, referrerIdPrefix);

    // Update popup with generated Search URL
    window['popup'].location.assign(url);
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <UitkLayoutFlex direction="column" justifyContent="space-around" alignItems="stretch" space="six">
        <UitkLayoutFlexItem className={classNames({ 'is-visually-hidden': lobs.length > 1 })}>
          <div>
            <Title>
              <LocalizedText message="product.search.title.heading.content.search_for_flights" />
            </Title>
          </div>
        </UitkLayoutFlexItem>

        <UitkLayoutFlexItem>
          <div>
            <Destinations />
          </div>
        </UitkLayoutFlexItem>

        <UitkLayoutFlexItem>
          <div>
            <Dates
              customStartLabel={l10n.formatText('product.search.datePicker.fromDate.customLabel')}
              customEndLabel={l10n.formatText('product.search.datePicker.toDate.customLabel')}
            />
          </div>
        </UitkLayoutFlexItem>

        <UitkLayoutFlexItem>
          <div>
            <UitkLayoutFlex direction="row" justifyContent="space-between" alignItems="start" space="six" wrap="wrap">
              <UitkLayoutFlexItem>
                <div>
                  <Branding />
                </div>
              </UitkLayoutFlexItem>

              <UitkLayoutFlexItem>
                <div>
                  <Submit isLoading={isLoading} />
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </div>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    </form>
  );
}

export { FlightsForm };
